'use client';

import {
  cloneElement,
  isValidElement,
  type ReactElement,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Tooltip, type TooltipProps } from '@mui/material';

interface TruncatedTooltipProps extends Omit<TooltipProps, 'children' | 'title'> {
  title: NonNullable<React.ReactNode>;
  children: ReactElement;
}

/**
 * Wraps a single-line/clamped text element and only shows the tooltip when
 * its content is actually overflowing (scrollWidth/scrollHeight > the
 * rendered box) — an empty `title` makes MUI's Tooltip a no-op wrapper, so a
 * fully-visible title never triggers a hover popup.
 */
export const TruncatedTooltip = ({ title, children, ...tooltipProps }: TruncatedTooltipProps) => {
  const elementRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const checkTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    };

    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title]);

  if (!isValidElement(children)) return children;

  const { slotProps, ...restTooltipProps } = tooltipProps;
  const mergedSlotProps = {
    // Default high enough to clear this app's Portal-rendered overlays
    // (search dropdown / modals sit at zIndex 10000-10001) — otherwise
    // MUI's default tooltip zIndex (~1500) renders invisibly behind them.
    // Follows the same convention as WithdrawModal's own 10002 popper.
    ...slotProps,
    popper: { sx: { zIndex: 10002 }, ...slotProps?.popper },
  };

  return (
    <Tooltip title={isTruncated ? title : ''} slotProps={mergedSlotProps} {...restTooltipProps}>
      {cloneElement(children, { ref: elementRef } as React.RefAttributes<HTMLElement>)}
    </Tooltip>
  );
};

export default TruncatedTooltip;
